import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, Clock, User, Newspaper, X } from 'lucide-react';
import { blogPosts } from '../mock';

export const Blog = () => {
  const [filter, setFilter] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const categories = ['All', 'Cloud Services', 'Web Development', 'Mobile Development', 'DevOps', 'Development', 'Security'];

  const filteredPosts = filter === 'All' 
    ? blogPosts 
    : blogPosts.filter(p => p.category === filter);

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPost(null), 300);
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12 animate-slide-up">
          <div className="w-20 h-20 bg-[rgba(0,168,255,0.2)] rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.5)' }}>
            <Newspaper className="text-[#00a8ff]" size={40} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold led-text animate-led-pulse mb-6" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
            OUR BLOG
          </h1>
          <p className="text-xl text-[#00a8ff]" style={{ textShadow: '0 0 10px #00a8ff' }}>
            Insights, tutorials, and industry trends from our experts
          </p>
        </div>

        {/* Filter Section */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-3 rounded-lg text-sm font-bold transition-all duration-300 border-2 ${
                filter === category
                  ? 'bg-[#00a8ff] text-[#0a2540] border-[#00a8ff] shadow-[0_0_20px_#00a8ff]'
                  : 'bg-[rgba(0,168,255,0.1)] text-[#00a8ff] border-[#00a8ff] hover:bg-[rgba(0,168,255,0.2)]'
              }`}
              style={ filter === category ? { boxShadow: '0 0 20px #00a8ff' } : {} }
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Grid - ALWAYS VISIBLE WITH CLICK */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post, idx) => (
            <Card key={post.id} onClick={() => handlePostClick(post)} className="group bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl overflow-hidden hover:shadow-[0_0_40px_#00a8ff] transition-all duration-300 hover:-translate-y-2 cursor-pointer" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
              <div className="relative overflow-hidden aspect-video bg-[rgba(10,37,64,0.7)]">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-80"
                 width={1200} height={800} />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#00a8ff] text-[#0a2540] border-0 font-bold" style={{ boxShadow: '0 0 15px #00a8ff' }}>{post.category}</Badge>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-3 led-text group-hover:text-[#00a8ff] transition-colors" style={{ textShadow: '0 0 8px #00a8ff' }}>
                  {post.title}
                </h3>
                <p className="text-[#00a8ff] text-sm mb-4" style={{ textShadow: '0 0 5px #00a8ff' }}>{post.excerpt}</p>
                
                <div className="flex items-center justify-between text-xs text-[#00a8ff]">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <User size={14} />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock size={14} />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#00a8ff] text-lg" style={{ textShadow: '0 0 8px #00a8ff' }}>No blog posts found in this category.</p>
          </div>
        )}

        {/* Blog Detail Modal with Zoom */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[rgba(10,37,64,0.98)] border-4 border-[#00a8ff] text-white" style={{ boxShadow: '0 0 60px rgba(0,168,255,0.9)' }}>
            {selectedPost && (
              <>
                <DialogHeader className="relative">
                  <button 
                    onClick={handleCloseModal}
                    className="absolute right-0 top-0 text-[#00a8ff] hover:text-white transition-colors"
                  >
                    <X size={32} />
                  </button>
                  <DialogTitle className="text-3xl font-bold led-text pr-12" style={{ textShadow: '0 0 15px #00a8ff' }}>
                    {selectedPost.title}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-6">
                  {/* Image Zoom */}
                  <div className="relative overflow-hidden rounded-xl border-3 border-[#00a8ff]" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.5)' }}>
                    <img
                      src={selectedPost.image}
                      alt={selectedPost.title}
                      className="w-full h-auto object-cover transition-transform duration-500 hover:scale-110"
                     width={1200} height={800} />
                    <Badge className="absolute top-4 left-4 bg-[#00a8ff] text-[#0a2540] border-0 font-bold text-base px-4 py-2" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                      {selectedPost.category}
                    </Badge>
                  </div>

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-6 text-[#00a8ff] text-base" style={{ textShadow: '0 0 5px #00a8ff' }}>
                    <div className="flex items-center space-x-2">
                      <User size={20} />
                      <span className="font-semibold">{selectedPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar size={20} />
                      <span>{new Date(selectedPost.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock size={20} />
                      <span>{selectedPost.readTime}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="bg-[rgba(0,168,255,0.1)] p-8 rounded-xl border-2 border-[#00a8ff]" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
                    <p className="text-[#00a8ff] text-lg leading-relaxed mb-6" style={{ textShadow: '0 0 5px #00a8ff' }}>
                      {selectedPost.excerpt}
                    </p>
                    
                    <div className="space-y-4 text-white text-base leading-relaxed">
                      <p>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. 
                        Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                      </p>
                      <p>
                        Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. 
                        Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                      </p>
                      <h3 className="text-2xl font-bold text-[#00a8ff] mt-6 mb-3" style={{ textShadow: '0 0 10px #00a8ff' }}>Key Takeaways</h3>
                      <ul className="list-disc list-inside space-y-2 text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>
                        <li>Understanding the latest trends in {selectedPost.category.toLowerCase()}</li>
                        <li>Best practices for implementing modern solutions</li>
                        <li>How to optimize your development workflow</li>
                        <li>Tools and technologies that make a difference</li>
                      </ul>
                      <p className="mt-6">
                        Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, 
                        totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
